import { AccountSelectPanelComponent } from '../account-select-panel/account-select-panel.component';
import { IconSvgComponent } from '@app/shared/icon-svg/icon-svg.component';
import { ChangeDetectionStrategy, Component, computed, ElementRef, inject, Input, input, output, signal, viewChild } from '@angular/core';
import { catchError, EMPTY, Observable } from 'rxjs';
import { AccountSessionState } from '@app/common/services/account-session-state.service';
import { AccountSwitchOutcome, AccountSwitchService } from '@app/common/services/account-switch.service';
import { BroadcastService } from '@app/common/services/broadcast.service';
import { ErrorHandlerService } from '@app/core/services';
import { IcAccount } from '@app/api/account/models/ic-account.model';
import { ICentralUserInfo } from '@app/shared/models';
import { Router } from '@angular/router';
import { UserPreferenceManagerService } from '@app/api/user-preference/user-preference-manager.service';
import { ToastService } from '@app/shared/services/toast.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';


@Component({
    host: {
        '(document:click)': 'onDocumentClick($event)',
        '(document:keydown.escape)': 'onEscapeKey()',
        '[class.ic-account-tb-host--header]': 'placement() === \'header\'',
    },
    selector: 'ic-toolbar-account-select',
    templateUrl: './toolbar-account-select.component.html',
    styleUrls: ['./toolbar-account-select.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [AccountSelectPanelComponent, IconSvgComponent, TranslatePipe]
})
export class ToolbarAccountSelectComponent {
    private accountSwitch = inject(AccountSwitchService);
    private broadcastService = inject(BroadcastService);
    private errorHandler = inject(ErrorHandlerService);
    private router = inject(Router);
    private session = inject(AccountSessionState);
    private toastService = inject(ToastService);
    private translate = inject(TranslateService);
    private userPreferenceManager = inject(UserPreferenceManagerService);

    readonly selectionMade = output<string>();

    /**
     * Where this toolbar is mounted. The sidebar passes nothing and keeps the default; the header
     * opts in with placement="header", which hangs the panel from the account segment and renders
     * it compact. Set on this component's own root, because emulated encapsulation cannot match a
     * class placed on its host from outside. The host binds its own header class, which lets it fill
     * the segment.
     */
    readonly placement = input<'header' | 'sidebar'>('sidebar');

    // Definite: set by the user @Input setter, which the header always binds.
    private _user!: ICentralUserInfo;
    // TODO: Skipped for migration because:
    //  Accessor inputs cannot be migrated as they are too complex.
    @Input() set user(value: ICentralUserInfo | undefined) {
        if (value == null) { return; }

        this._user = value;
        this.getComponentData();
    }

    get user(): ICentralUserInfo {
        return this._user;
    }

    private readonly panelTrigger = viewChild<ElementRef<HTMLElement>>('accountPanelTrigger');
    private readonly panelOverlay = viewChild<ElementRef<HTMLElement>>('accountPanel');

    protected readonly isPanelOpen = signal(false);

    /**
     * Ids whose sub-accounts are showing. Held here because the panel is deliberately stateless
     * about expansion; it is a set in all but type, and nothing outside this component reads it.
     */
    protected readonly expandedAccountIds = signal<readonly string[]>([]);

    // The session's account tree and the account in effect, read straight from AccountSessionState. AccountSwitchService
    // is its only writer, so the header's and the sidebar's toolbars name the same account with no per-instance copy.
    protected readonly accounts = this.session.accounts;
    protected readonly selectedAccount = this.session.selectedAccount;
    protected readonly selectedAccountId = this.session.selectedAccountId;

    /**
     * What replaces the dropdown's `[disabled]="accountsList?.length < 2"`. With nothing to
     * switch to the panel never opens - but the trigger still names the account in effect, which
     * a hidden control would take away, and which is the one thing a single-account user still
     * needs from this control. Counted off the whole tree (switchableAccountCount), so a single
     * main account with sub-accounts still opens.
     */
    protected readonly isInteractive = computed(() => this.session.switchableAccountCount() > 1);

    // =========================================================================================================================================================
    // Event Handlers
    // =========================================================================================================================================================

    protected onTriggerClick(event: Event) {
        // Without this the document listener below sees the same click and closes it again.
        event.stopPropagation();

        if (!this.isInteractive()) { return; }

        this.isPanelOpen.update(open => !open);
    }

    /**
     * Enter and Space on the trigger do what a click does. Both are needed because the trigger
     * is a `div` carrying `role="button"`: a real `<button>` would synthesise a click from
     * either key, and a div synthesises neither, so the two keys the role promises have to be
     * honoured by hand or the control is mouse-only.
     *
     * Both are swallowed. Space would otherwise scroll the page out from under the overlay it
     * has just opened, and Enter would submit any form the header ever ends up inside.
     *
     * Focus is not moved into the panel from here. The panel takes focus itself when it is
     * created (`ngAfterViewInit`), which is the same moment for every way of opening it, and
     * which keeps this component out of the panel's DOM.
     */
    protected onTriggerKeydown(event: KeyboardEvent) {
        if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') { return; }

        event.preventDefault();
        this.onTriggerClick(event);
    }

    /**
     * A row was chosen. Everything past this point is the switch machine, unchanged.
     *
     * A payment-locked account is refused here rather than in the panel, which keeps the panel
     * presentational and puts the decision next to the state machine that would otherwise act on
     * it. The refusal is silent and total - no persist, no broadcast, no navigate - and matches
     * `updateSelectedAccounts()` on /account/account-select, which has returned early on
     * `paymentLockout` since it shipped. The header must not be the more permissive of the two.
     *
     * The panel is deliberately left OPEN on a refusal. Closing it would read as the click having
     * been accepted, and it would take the row's PAY NOW link off screen at the exact moment the
     * user has been told to use it.
     *
     * The refusal is now belt AND braces: since PI-ICNT-SW-680 the panel's row body is itself
     * inert on a locked row, so no locked id arrives here from a row press any more. It stays
     * because this is where the billing rule lives and because ids reach this method from more
     * than one place -- deleting it as unreachable would move the rule into a presentational
     * component and leave nothing behind to enforce it.
     */
    protected onPanelAccountSelected(accountId: string) {
        const picked = this.findListItem(accountId);

        if (picked != null && this.isPaymentLocked(picked)) { return; }

        // Focus goes back to the trigger, which now names the account just chosen. There is no
        // row left to hold it -- the panel is being destroyed -- and the trigger is the control
        // the user operated, so it is where the next Tab should carry on from.
        this.closePanel(true);

        if (picked == null) { return; }

        this.onUserSelectedAccount(picked);
    }

    /**
     * The panel reported PAY NOW. The anchor's own routerLink does the navigating - this only
     * gets the overlay out of the way, so the user does not land on Billing with the account
     * panel still hanging over it.
     */
    protected onPanelPayNow() {
        this.isPanelOpen.set(false);
    }

    /**
     * The person-gear on a row was pressed: show that account's details page. The same route the
     * full /account/account-select page uses for the same gesture (`account-select.component.ts`
     * `navigate()`), so the two entry points cannot drift apart.
     *
     * The id arrives from the row that was pressed, so a sub-account row sends its own id rather
     * than its parent's - nothing here re-resolves it, because there is nothing to resolve: this
     * navigates, it does not select.
     *
     * Emphatically not a switch. No persist, no broadcast, no selectionMade - opening an
     * account's details is not working in it, and a person-gear press that quietly moved the
     * user into a different account would be this panel's worst possible bug. It is also why
     * lockout is not consulted here: a payment-locked account is one its owner especially needs
     * to be able to open.
     */
    protected onPanelAccountSettings(accountId: string) {
        this.isPanelOpen.set(false);

        if (accountId == null) { return; }

        this.router.navigate(['/account/account-view/' + accountId]);
    }

    /**
     * The panel header's ACCOUNT SELECT button was pressed: open the full page. That page is not
     * being retired - it keeps its own sidebar entry and its own deep links - so this is a plain
     * deep link into it and changes nothing about the account in effect.
     */
    protected onPanelAccountSelectClicked() {
        this.isPanelOpen.set(false);
        this.router.navigate(['/account/account-select']);
    }

    protected onPanelExpandToggled(accountId: string) {
        this.expandedAccountIds.update(ids => ids.includes(accountId) ? ids.filter(id => id !== accountId) : [...ids, accountId]);
    }

    onDocumentClick(event: MouseEvent) {
        if (!this.isPanelOpen()) { return; }

        const target = event.target as Node;
        const clickedInsideTrigger = this.panelTrigger()?.nativeElement.contains(target);
        const clickedInsidePanel = this.panelOverlay()?.nativeElement.contains(target);

        if (!clickedInsideTrigger && !clickedInsidePanel) {
            this.isPanelOpen.set(false);
        }
    }

    /**
     * Escape dismisses, and hands focus back to the trigger.
     *
     * The return is the half that is usually left out, and without it the panel is operable
     * but not usable: focus was inside a subtree that has just been destroyed, so the browser
     * drops it on the body and the next Tab restarts from the top of the document rather than
     * from the control the user was just working.
     *
     * Guarded on the panel actually being open so that Escape pressed anywhere else on the
     * page -- closing some other overlay, cancelling some other edit -- does not yank focus
     * into the header.
     */
    onEscapeKey() {
        if (!this.isPanelOpen()) { return; }

        this.closePanel(true);
    }

    // =========================================================================================================================================================
    // Helper Methods
    // =========================================================================================================================================================

    /**
     * Shut the panel, optionally putting focus back on the trigger.
     *
     * Focus is returned when the user dismissed or chose from the keyboard's point of view --
     * Escape, or a selection -- and deliberately NOT when the panel was dismissed by a click
     * somewhere else on the page, where the user has already said where they want to be and
     * pulling them back to the header would be the control talking over them.
     */
    private closePanel(returnFocusToTrigger: boolean) {
        this.isPanelOpen.set(false);

        if (returnFocusToTrigger) {
            this.panelTrigger()?.nativeElement.focus();
        }
    }

    private getComponentData() {

        if (this.user.isToroUser()) { return; }

        // Boot selection belongs to AccountSwitchService: it resolves the stored default once for both toolbars, writes
        // it into AccountSessionState and announces it once. This only starts or joins it, so it broadcasts nothing at boot.
        this.accountSwitch.getInitialSelection()
            .subscribe({ error: err => this.errorHandler.handleError(err) });
    }

    private onUserSelectedAccount(value: IcAccount) {
        // The trigger keeps naming the account in effect until the switch is saved: the save broadcasts the new id and
        // AccountSwitchService folds it into AccountSessionState, which the trigger reads. Naming the pick straight away left the header claiming the new
        // account while the page's unsaved-changes dialog was still asking whether to leave.
        this.selectionMade.emit(value.accountID);
        this.switchAccount(value.accountID)
            .subscribe((outcome: AccountSwitchOutcome) => {
                // Only a switch this toolbar started and saved announces itself. A cancelled switch changed nothing, and
                // a superseded one is announced by the newer switch that overtook it.
                if (outcome === 'switched') {
                    this.toastService.showSimpleSuccess(this.translate.instant('STRINGS.ACCOUNT_SWITCHED_TO', { accountName: value.accountName }));
                }
            });
    }

    /**
     * The switch itself - asking the page, persist-then-broadcast, and re-creating the page for the
     * new account so the user stays where they are - lives in AccountSwitchService.switchTo. This
     * only decides whether a save is needed, and settles the dropdown if it fails. A save is skipped
     * only when the id is already the persisted default and no other switch is in flight - a pending
     * switch may land after this broadcast. A skipped save is not a switch, so the page is neither
     * asked nor re-created. The outcome is handed back so a pick the page refused can be undone.
     */
    private switchAccount(accountId: string): Observable<AccountSwitchOutcome> {
        if (this.userPreferenceManager.defaultAccount === accountId && !this.userPreferenceManager.hasPendingSwitch) {
            this.broadcastService.selectedAccountChange.next(accountId);
            return EMPTY;
        }

        return this.accountSwitch.switchTo(accountId)
            .pipe(
                catchError(err => {
                    // Nothing was persisted or broadcast. Settle on the last persisted account, or the
                    // first in the list when that is unset or unknown, and tell the app; AccountSwitchService
                    // folds the broadcast into AccountSessionState, so what is on screen and what the app
                    // holds agree.
                    const settled = this.findListItem(this.userPreferenceManager.defaultAccount) ?? this.session.accounts()[0];
                    if (settled != null) {
                        this.broadcastService.selectedAccountChange.next(settled.accountID);
                    }
                    this.errorHandler.handleError(err);
                    return EMPTY;
                })
            );
    }

    /**
     * Resolve an id against the flattened tree (AccountSessionState.resolvableAccounts), so a
     * sub-account is found as readily as a main one. Both ids that reach this component come
     * through here - a user pick and the settle after a failed save - so a sub-account is a
     * first-class selection on both. A broadcast from elsewhere in the app and the stored default
     * at boot are resolved against the same flattened tree, in AccountSwitchService.
     */
    private findListItem(accountId: string | undefined): IcAccount | undefined {
        if (accountId == null) { return undefined; }
        return this.session.resolvableAccounts().find(a => a.accountID === accountId);
    }

    /**
     * Whether this one account is payment-locked. Read off the account itself and never off its
     * parent or its children: lockout is a per-account billing state and is not inherited in
     * either direction, so an unlocked sub-account of a locked main account stays selectable and
     * a locked child of an unlocked parent does not. Because `findListItem` resolves against the
     * flattened list, the same check covers main accounts and sub-accounts alike.
     *
     * Note what is deliberately absent: nothing anywhere in this component re-checks the current
     * selection against this. An account that becomes locked while the user is sitting on it is
     * left selected - AccountSwitchService's fold of a broadcast and its rebuild on an account edit
     * both re-point at the new instance by id and neither consults lockout. Moving a user off the account they
     * were working on, without being asked, would be a wrong-account bug wearing a helpful face;
     * the badge already explains the situation and PAY NOW already offers the way out.
     */
    private isPaymentLocked(account: IcAccount): boolean {
        return account?.paymentLockout === true;
    }
}
